import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ArrowUpRight, Zap, Cpu, Network, Phone, MessagesSquare, Workflow, CalendarCheck,
  ShieldCheck, Database, GitBranch, FileBarChart2, Sparkles,
  Globe2, Users, Send, Lock,
} from "lucide-react";
import OrbitalEcosystem from "@/components/marketing/OrbitalEcosystem";

const PILLARS = [
  {
    n: "01", Icon: Zap, name: "Customer Acquisition", tag: "Primary product",
    intro: "100% exclusive, high-intent enquiries delivered in real-time. Live-transfer, AI-qualified and appointment-ready — across ten financial verticals. Every lead sold to one buyer only, never resold.",
    bullets: ["Real-time delivery in ≤1 second", "Ping-tree and direct-post routing", "TrustedForm, LeadID & GDPR consent", "Geo, demo, affordability filters"],
    href: "/buy-leads", cta: "Buy exclusive leads",
  },
  {
    n: "02", Icon: Cpu, name: "AI Operational Systems", tag: "AI layer",
    intro: "Voice, chat, WhatsApp and omnichannel orchestration. Compliance-scripted, sub-second, and CRM-native.",
    bullets: ["Sub-300ms voice agent latency", "WhatsApp Business automation", "Calendar-aware appointment booker", "FCA + TCPA compliant scripts"],
    href: "/ai-systems", cta: "Explore AI systems",
  },
  {
    n: "03", Icon: Network, name: "Enterprise Infrastructure", tag: "The backbone",
    intro: "Client portals, ping trees, billing, affiliate management, compliance audit logs, and reporting — operated as one platform.",
    bullets: ["Role-based dashboards (client / admin / affiliate)", "Per-buyer caps, schedules & filters", "Webhook + REST + CSV delivery", "Full audit log + DSAR tools"],
    href: "/book-demo", cta: "Book a walkthrough",
  },
];

const FEATURES = [
  { Icon: Database,        t: "Direct-post & Ping/Post", d: "Run both delivery models in parallel on the same campaign." },
  { Icon: GitBranch,       t: "Ping Trees",              d: "Multi-buyer waterfall logic with caps, schedules and fallbacks." },
  { Icon: ShieldCheck,     t: "Compliance Layer",        d: "TrustedForm, Anura, Xverify, TCPA, GDPR — built in." },
  { Icon: FileBarChart2,   t: "Live Analytics",          d: "Per-source, per-buyer P&L with cohort & funnel views." },
  { Icon: Phone,           t: "Voice + Transfer",        d: "Live phone transfer with whisper, recording and disposition." },
  { Icon: MessagesSquare,  t: "Conversational AI",       d: "Web, SMS and WhatsApp agents tuned to each sector." },
  { Icon: Workflow,        t: "CRM Workflows",           d: "HubSpot, Salesforce, Pipedrive, GoHighLevel — bi-directional." },
  { Icon: CalendarCheck,   t: "Appointment Engine",      d: "AI agent books straight into your closer's calendar." },
];

export default function Solutions() {
  return (
    <div>
      {/* ── HERO — text + orbital ecosystem ─────────────────── */}
      <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/30">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="absolute -top-32 -right-20 w-[700px] h-[700px] rounded-full bg-[#3d7bff]/[0.07] blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            {/* LEFT — copy + CTAs */}
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Solutions
              </div>
              <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[64px] tracking-[-0.045em] font-semibold text-slate-900 leading-[1.02]">
                The operating layer for{" "}
                <span className="bg-gradient-to-r from-slate-900 via-[#0055FF] to-[#3d7bff] bg-clip-text text-transparent">
                  modern financial brands.
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-[17px] text-slate-600 leading-relaxed">
                Three pillars, one integrated product — designed for operators who treat acquisition, automation and infrastructure as a single discipline.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/buy-leads">
                  <Button data-testid="solutions-buy-cta" className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]">
                    Buy exclusive leads <ArrowUpRight className="size-4" />
                  </Button>
                </Link>
                <Link to="/sell-leads">
                  <Button variant="outline" data-testid="solutions-sell-cta" className="rounded-full px-5 h-11 border-slate-300 hover:border-[#0055FF]/40">
                    Sell leads to our network
                  </Button>
                </Link>
                <Link to="/book-demo" className="text-[13px] font-medium text-[#0055FF] hover:underline inline-flex items-center gap-1 ml-1">
                  Book strategy call <ArrowRight className="size-3.5" />
                </Link>
              </div>

              {/* Inline trust strip */}
              <div className="mt-10 pt-8 border-t border-slate-200 grid grid-cols-3 gap-6 max-w-md">
                <Stat v="240k+" l="enquiries / mo" />
                <Stat v="98.6ms" l="median routing" />
                <Stat v="99.99%" l="uptime" />
              </div>
            </div>

            {/* RIGHT — orbital ecosystem */}
            <div className="relative">
              <OrbitalEcosystem />
              {/* Legend */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-center text-slate-500">
                <LegendChip color="bg-white border border-slate-200" label="Sectors" />
                <LegendChip color="bg-[#0055FF]" label="AI agents" />
                <LegendChip color="bg-slate-900" label="Infrastructure" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRIMARY ACTION BAND REMOVED — CTAs already live in the hero ── */}

      {/* ── THREE PILLARS ───────────────────────────────────── */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">The three pillars</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.035em] font-semibold text-slate-900 leading-[1.02]">
              What runs underneath the orbit.
            </h2>
          </div>

          <div className="space-y-6">
            {PILLARS.map((p, i) => (
              <Pillar key={i} {...p} />
            ))}
          </div>
        </div>
      </section>

      {/* ── BENTO FEATURE GRID (cal.com inspired) ───────────── */}
      <section className="py-24 bg-slate-50/60 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-12">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Everything in one stack</p>
              <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.035em] font-semibold text-slate-900 leading-[1.02]">
                Designed for operators, not analysts.
              </h2>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600">
              <Sparkles className="size-3 text-[#0055FF]" /> 8 modules · 1 platform
            </div>
          </div>

          {/* Bento — first card spans 2 cols + 2 rows on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 lg:auto-rows-[180px] gap-4">
            {/* HERO BENTO CARD — fixed-price routing visualization */}
            <div className="col-span-2 row-span-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-[#0c1b3a] text-white p-5 sm:p-7 relative overflow-hidden group" data-testid="bento-hero">
              <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#3d7bff]/30 blur-3xl pointer-events-none" />

              <div className="relative h-full flex flex-col">
                <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#7da9ff] font-semibold">
                  <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live exclusive routing
                </div>
                <h3 className="mt-3 text-[22px] sm:text-[26px] tracking-[-0.025em] font-semibold leading-[1.1]">
                  Every lead routed to its exclusive buyer in <span className="bg-gradient-to-r from-white to-[#7da9ff] bg-clip-text text-transparent">under 100ms.</span>
                </h3>

                <FixedPriceRoutingViz />

                <div className="mt-auto pt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-white/55">
                  <span>Sourced from our funnels, affiliates &amp; publishers</span>
                  <span className="text-white/30">·</span>
                  <span><span className="text-emerald-400 font-mono">Fixed</span> price per lead</span>
                  <span className="text-white/30">·</span>
                  <span><span className="text-white font-mono">1</span> exclusive buyer</span>
                </div>
              </div>
            </div>

            {/* Smaller bento cards */}
            {FEATURES.map((f, i) => (
              <BentoCard key={i} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h3 className="text-3xl sm:text-4xl tracking-[-0.02em] font-semibold text-slate-900">See how it routes for your sector.</h3>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            A 30-minute working session with our infrastructure team — bring your numbers, leave with a routing plan.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/book-demo">
              <Button data-testid="solutions-book-demo" className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]">
                Book strategy call <ArrowRight className="size-4" />
              </Button>
            </Link>
            <Link to="/buy-leads">
              <Button variant="outline" className="rounded-full px-5 h-11 border-slate-300">
                Or buy leads now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── sub-components ──────────────────────────────────────── */

function Stat({ v, l }) {
  return (
    <div>
      <div className="text-[22px] font-semibold tracking-[-0.025em] text-slate-900 tabular-nums">{v}</div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">{l}</div>
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <div className="inline-flex items-center justify-center gap-1.5">
      <span className={`size-2 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
}

function ActionCard() { return null; }


function Pillar({ n, Icon, name, tag, intro, bullets, href, cta }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-7 lg:p-10 grid lg:grid-cols-12 gap-8 hover:border-[#0055FF]/30 hover:shadow-md transition-all" data-testid={`pillar-${n}`}>
      <div className="lg:col-span-4">
        <div className="flex items-center gap-3">
          <span className="text-[44px] leading-none font-semibold tracking-[-0.04em] text-[#0055FF] tabular-nums">{n}</span>
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 font-medium">{tag}</span>
        </div>
        <div className="mt-5 inline-grid place-items-center size-11 rounded-xl bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15">
          <Icon className="size-5" strokeWidth={1.7} />
        </div>
        <h3 className="mt-4 text-2xl sm:text-[28px] tracking-[-0.025em] font-semibold text-slate-900">{name}</h3>
        <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{intro}</p>
        <div className="mt-5">
          <Link to={href} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0055FF] hover:underline">
            {cta} <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
        {bullets.map((b, j) => (
          <div key={j} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 hover:border-[#0055FF]/30 hover:bg-white transition-colors">
            <div className="text-[10.5px] font-mono text-slate-400">0{j + 1}</div>
            <div className="mt-1.5 text-[14px] font-medium text-slate-900 leading-snug">{b}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BentoCard({ Icon, t, d }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#0055FF]/40 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15">
        <Icon className="size-5" strokeWidth={1.7} />
      </span>
      <div className="mt-4 font-semibold text-[14px] text-slate-900 tracking-tight">{t}</div>
      <div className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed">{d}</div>
    </div>
  );
}

function FixedPriceRoutingViz() {
  /* ───────── Fixed-price routing flow ─────────
     3-column layout: Sources (left)  →  Routing engine (centre)  →  Exclusive buyer (right)
     CSS Grid handles positioning; an absolutely-positioned SVG overlay draws
     the connecting animated flow lines + travelling packet.

     Animation choreography (single 4.8s loop):
       1. Source dots pulse + each source→core line draws inward (staggered 0.3s)
       2. Routing-core ring expands as packets "arrive"
       3. A single emerald packet travels from core to buyer
       4. Buyer card glows emerald + price tag fades in
  */
  const CYCLE = 4.8;
  const SOURCES = [
    { Icon: Globe2, label: "Our funnels" },
    { Icon: Users,  label: "Affiliates" },
    { Icon: Send,   label: "Publishers" },
  ];

  return (
    <div className="mt-5 relative" aria-hidden>
      <style>{`
        @keyframes srcDot {
          0%, 100% { opacity: 0.5; transform: scale(0.95); }
          50%      { opacity: 1;   transform: scale(1.1); }
        }
        @keyframes srcLineDraw {
          0%   { stroke-dashoffset: 100; opacity: 0; }
          15%  { opacity: 0.95; }
          50%  { stroke-dashoffset: 0;   opacity: 0.95; }
          70%  { opacity: 0.45; }
          100% { stroke-dashoffset: 0;   opacity: 0.45; }
        }
        @keyframes coreRingExpand {
          0%, 100% { opacity: 0; transform: scale(0.9); }
          55%      { opacity: 0.75; transform: scale(1); }
          75%      { opacity: 0; transform: scale(1.22); }
        }
        @keyframes outLineDraw {
          0%, 55%  { stroke-dashoffset: 80; opacity: 0; }
          70%      { opacity: 1; }
          95%, 100% { stroke-dashoffset: 0;  opacity: 1; }
        }
        @keyframes outPacketMove {
          0%, 60%  { offset-distance: 0%;   opacity: 0; }
          72%      { opacity: 1; }
          92%      { offset-distance: 100%; opacity: 1; }
          100%     { offset-distance: 100%; opacity: 0; }
        }
        @keyframes buyerGlow {
          0%, 78%  { box-shadow: 0 0 0 0 rgba(34,211,166,0); border-color: rgba(34,211,166,0.40); }
          90%      { box-shadow: 0 0 26px 4px rgba(34,211,166,0.45); border-color: rgba(34,211,166,0.95); }
          100%     { box-shadow: 0 0 0 0 rgba(34,211,166,0); border-color: rgba(34,211,166,0.40); }
        }
        @keyframes priceFade {
          0%, 82% { opacity: 0.35; transform: translateY(1px); }
          92%     { opacity: 1; transform: translateY(0); }
          100%    { opacity: 1; }
        }
      `}</style>

      {/* 3-column grid: SOURCES | CORE | BUYER */}
      <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-5">

        {/* LEFT — 3 source pills */}
        <div className="flex flex-col gap-2 z-10 relative min-w-0">
          {SOURCES.map(({ Icon, label }, i) => (
            <div
              key={label}
              className="flex items-center gap-2 rounded-md bg-white/[0.06] ring-1 ring-white/15 px-2.5 py-1.5 backdrop-blur w-fit"
            >
              <span
                className="grid place-items-center size-6 rounded bg-[#0055FF]/15 text-[#7da9ff]"
                style={{ animation: `srcDot 2.4s ease-in-out ${i * 0.3}s infinite` }}
              >
                <Icon className="size-3.5" strokeWidth={1.8} />
              </span>
              <span className="text-[11px] font-medium text-white/85 whitespace-nowrap pr-1">{label}</span>
            </div>
          ))}
        </div>

        {/* CENTRE — Connexis routing engine */}
        <div className="relative z-10 flex flex-col items-center">
          <div className="relative size-[56px]">
            <span
              className="absolute inset-0 rounded-xl border border-[#3d7bff]"
              style={{ animation: `coreRingExpand ${CYCLE}s ease-in-out infinite` }}
            />
            <span className="absolute inset-0 grid place-items-center rounded-xl bg-[#0a1124] ring-1 ring-white/20 shadow-[0_0_22px_rgba(0,85,255,0.4)]">
              <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 7h7M5 12h12M5 17h7" strokeLinecap="round" stroke="#ffffff" />
                <circle cx="18" cy="7"  r="2.2" fill="#3d7bff" stroke="none" />
                <circle cx="18" cy="17" r="2.2" fill="#3d7bff" stroke="none" />
              </svg>
            </span>
          </div>
          <div className="mt-1.5 text-center text-[9px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-white/55 font-mono whitespace-nowrap">
            Routing engine
          </div>
        </div>

        {/* RIGHT — single exclusive buyer */}
        <div className="relative z-10 flex justify-end min-w-0">
          <div
            className="relative w-full max-w-[124px] sm:max-w-[150px] rounded-lg bg-emerald-500/[0.08] border border-emerald-400/40 px-3 py-2.5"
            style={{ animation: `buyerGlow ${CYCLE}s ease-in-out infinite` }}
          >
            <div className="flex items-center gap-1.5">
              <Lock className="size-3 text-emerald-300" strokeWidth={2.2} />
              <span className="text-[9.5px] uppercase tracking-[0.16em] text-emerald-300 font-mono font-semibold">
                Exclusive
              </span>
            </div>
            <div className="mt-1 text-[12.5px] font-semibold text-white tracking-tight leading-tight">
              Matched buyer
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-1">
              <span className="text-[10.5px] text-white/55">Mortgages</span>
              <span
                className="text-[14px] font-mono font-semibold text-emerald-300 tabular-nums"
                style={{ animation: `priceFade ${CYCLE}s ease-in-out infinite` }}
              >
                £38
              </span>
            </div>
          </div>
        </div>

        {/* ABSOLUTE SVG OVERLAY — connecting flow lines + travelling packet
             stretched over the whole grid, behind content (z-0). The grid columns
             are 1fr/auto/1fr so the SVG just uses viewBox 0..100 in x and the
             three CSS y-positions map to ~16% / 50% / 84% of the grid height. */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full z-0 pointer-events-none"
        >
          <defs>
            <linearGradient id="srcLineGradV2" x1="0" x2="1">
              <stop offset="0%"   stopColor="#3d7bff" stopOpacity="0" />
              <stop offset="60%"  stopColor="#3d7bff" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#3d7bff" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="outLineGradV2" x1="0" x2="1">
              <stop offset="0%"   stopColor="#22D3A6" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#22D3A6" stopOpacity="0.45" />
            </linearGradient>
          </defs>

          {/* Three source→core curves */}
          {[16, 50, 84].map((yStart, i) => (
            <path
              key={i}
              d={`M 22 ${yStart} C 38 ${yStart}, 42 50, 50 50`}
              stroke="url(#srcLineGradV2)"
              strokeWidth="0.6"
              fill="none"
              strokeLinecap="round"
              strokeDasharray="100"
              vectorEffect="non-scaling-stroke"
              style={{ animation: `srcLineDraw ${CYCLE}s ease-in-out ${i * 0.3}s infinite` }}
            />
          ))}

          {/* Core→buyer line */}
          <path
            d="M 56 50 C 64 50, 70 50, 78 50"
            stroke="url(#outLineGradV2)"
            strokeWidth="0.9"
            fill="none"
            strokeLinecap="round"
            strokeDasharray="80"
            vectorEffect="non-scaling-stroke"
            style={{ animation: `outLineDraw ${CYCLE}s ease-in-out infinite` }}
          />

          {/* Travelling emerald packet from core to buyer */}
          <circle
            r="1.4"
            fill="#22D3A6"
            style={{
              offsetPath: "path('M 56 50 C 64 50, 70 50, 78 50')",
              animation: `outPacketMove ${CYCLE}s ease-in-out infinite`,
              filter: "drop-shadow(0 0 4px rgba(34,211,166,0.8))",
            }}
          />
        </svg>
      </div>
    </div>
  );
}
